import {
	type ProjectInput,
	type ProjectMemberRole,
	WHO_CAN_ADD_TASKS_EVERYONE,
	WHO_CAN_ADD_TASKS_PM_ONLY,
	WHO_CAN_ADD_TASKS_PROJECT_TEAM,
	type WhoCanAddTasks,
} from "@timeghost/shared";

export interface ProjectClient {
	readonly color: string | null;
	readonly id: string;
	readonly name: string;
	readonly useAsDefault: boolean;
}

export interface ProjectTag {
	readonly color?: string | null;
	readonly id: string;
	readonly name: string;
}

export interface ProjectTimesRecorded {
	readonly billable: number;
	readonly notBillable: number;
}

export interface ProjectUser {
	readonly billableRate: number | null;
	readonly id: string;
	readonly removed: boolean;
	readonly role: ProjectMemberRole;
}

export interface ProjectGroup {
	readonly id: string;
	readonly removed: boolean;
	readonly role: ProjectMemberRole;
}

export type ProjectBudgetType = "task" | "manual";

export interface Project {
	readonly billable: boolean;
	readonly client: ProjectClient | null;
	readonly color: string | null;
	readonly completed: boolean;
	readonly description: string;
	readonly estimation: number;
	readonly externalId: string;
	readonly groups: readonly ProjectGroup[];
	readonly hourlyRate: number | null;
	readonly hourlyRateEnabled: boolean;
	readonly id: string;
	readonly name: string;
	readonly private: boolean;
	readonly projectType: "project" | "template";
	readonly raw: Record<string, unknown>;
	readonly tags: readonly ProjectTag[];
	readonly taskBasedEstimation: boolean;
	readonly taskCount: number;
	readonly timesRecorded: ProjectTimesRecorded | null;
	readonly useAsDefault: boolean;
	readonly users: readonly ProjectUser[];
	readonly whoCanAddTasks: WhoCanAddTasks;
}

export type ProjectAdd = ProjectInput;

export type ProjectUpdate = Record<string, unknown> & {
	readonly id: string;
};

const asString = (value: unknown, fallback = ""): string => (typeof value === "string" ? value : fallback);

const asNumber = (value: unknown): number => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}
	if (typeof value === "string" && value.length > 0) {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : 0;
	}
	return 0;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
	if (!value || typeof value !== "object") {
		return null;
	}
	return value as Record<string, unknown>;
};

const parseClient = (value: unknown): ProjectClient | null => {
	const raw = asRecord(value);
	if (!raw) {
		return null;
	}
	const id = asString(raw.id).trim();
	const name = asString(raw.name).trim();
	if (!(id && name)) {
		return null;
	}
	return {
		id,
		name,
		color: typeof raw.color === "string" && raw.color.trim().length > 0 ? raw.color.trim() : null,
		useAsDefault: raw.useAsDefault === true,
	};
};

const parseTags = (value: unknown): readonly ProjectTag[] => {
	if (!Array.isArray(value)) {
		return [];
	}
	const result: ProjectTag[] = [];
	for (const item of value) {
		const raw = asRecord(item);
		if (!raw) {
			continue;
		}
		const id = asString(raw.id).trim();
		const name = asString(raw.name).trim();
		if (!(id && name)) {
			continue;
		}
		result.push({
			id,
			name,
			color: typeof raw.color === "string" && raw.color.trim().length > 0 ? raw.color.trim() : null,
		});
	}
	return result;
};

const parseRole = (value: unknown): ProjectMemberRole => {
	if (value === "manager" || value === 1) {
		return "manager";
	}
	return "member";
};

const parseUsers = (value: unknown): readonly ProjectUser[] => {
	if (!Array.isArray(value)) {
		return [];
	}
	const result: ProjectUser[] = [];
	for (const item of value) {
		const raw = asRecord(item);
		if (!raw) {
			continue;
		}
		const id = asString(raw.id).trim();
		if (!id) {
			continue;
		}
		const rateRaw = raw.billable_rate ?? raw.billableRate;
		const billableRate = typeof rateRaw === "number" && Number.isFinite(rateRaw) ? rateRaw : null;
		result.push({
			id,
			role: parseRole(raw.role),
			removed: raw.removed === true,
			billableRate,
		});
	}
	return result;
};

const parseGroups = (value: unknown): readonly ProjectGroup[] => {
	if (!Array.isArray(value)) {
		return [];
	}
	const result: ProjectGroup[] = [];
	for (const item of value) {
		const raw = asRecord(item);
		if (!raw) {
			continue;
		}
		const id = asString(raw.id).trim();
		if (!id) {
			continue;
		}
		result.push({
			id,
			role: parseRole(raw.role),
			removed: raw.removed === true,
		});
	}
	return result;
};

const parseTimesRecorded = (value: unknown): ProjectTimesRecorded | null => {
	const raw = asRecord(value);
	if (!raw) {
		return null;
	}
	return {
		billable: Math.max(0, asNumber(raw.billable)),
		notBillable: Math.max(0, asNumber(raw.notBillable)),
	};
};

const parseWhoCanAddTasks = (value: unknown): WhoCanAddTasks => {
	const n = typeof value === "number" ? value : Number(value);
	if (n === WHO_CAN_ADD_TASKS_EVERYONE || n === WHO_CAN_ADD_TASKS_PROJECT_TEAM) {
		return n;
	}
	return WHO_CAN_ADD_TASKS_PM_ONLY;
};

export const projectRecordedSeconds = (project: Project): number => {
	const recorded = project.timesRecorded;
	if (!recorded) {
		return 0;
	}
	if (project.billable) {
		return recorded.billable;
	}
	return recorded.billable + recorded.notBillable;
};

export const projectBudgetType = (project: Project): ProjectBudgetType =>
	project.taskBasedEstimation ? "task" : "manual";

export const parseProject = (raw: Record<string, unknown>): Project | null => {
	const id = asString(raw.id).trim();
	const name = asString(raw.name).trim();
	if (!(id && name)) {
		return null;
	}

	const hourlyEnabled =
		raw.hourly_rate_enabled === true ||
		raw.hourlyRatesEnabled === true ||
		(typeof raw.hourly_rate === "number" && raw.hourly_rate > 0);

	return {
		id: asString(raw.id).trim(),
		name: asString(raw.name).trim(),
		description: asString(raw.description).trim(),
		color: typeof raw.color === "string" && raw.color.trim().length > 0 ? raw.color.trim() : null,
		billable: raw.billable === true,
		private: raw.private === true,
		completed: raw.completed === true,
		useAsDefault: raw.useAsDefault === true,
		estimation: asNumber(raw.estimation),
		taskBasedEstimation: raw.taskBasedEstimation === true,
		externalId: asString(raw.externalId ?? raw.external_id).trim(),
		whoCanAddTasks: parseWhoCanAddTasks(raw.whoCanAddTasks ?? raw.canCreateTasks),
		hourlyRateEnabled: hourlyEnabled,
		hourlyRate: (() => {
			const rate = raw.hourly_rate ?? raw.hourlyRate;
			return typeof rate === "number" && Number.isFinite(rate) ? rate : null;
		})(),
		client: parseClient(raw.client),
		tags: parseTags(raw.tags),
		users: parseUsers(raw.users),
		groups: parseGroups(raw.groups),
		timesRecorded: parseTimesRecorded(raw.timesRecorded),
		taskCount: Math.max(0, Math.floor(asNumber(raw.taskCount))),
		projectType: raw.projectType === "template" ? "template" : "project",
		raw,
	};
};
